import { useEffect, useRef, useState, type MouseEvent, type RefObject } from 'react';
import { formatVoiceSeconds } from './voice-note-recorder';

// The playback half of an audio player, with no opinion about how it looks.
//
// Shared by the chat bubble (`AudioAttachmentPlayer`) and the doc viewer, which differ in chrome but share one rule:
//
// ONE audio element plays at a time, ACROSS surfaces. The lock is module-level here rather than per
// component, so starting a voice note in chat pauses a track playing in the viewer and vice versa. Two
// independent locks would let both play at once, which is the bug this arrangement exists to prevent.

let activeAudio: HTMLAudioElement | null = null;

function claimPlayback(el: HTMLAudioElement): void {
  if (activeAudio && activeAudio !== el) activeAudio.pause();
  activeAudio = el;
}

export interface AudioPlayer {
  ref: RefObject<HTMLAudioElement | null>;
  playing: boolean;
  /** Seconds, or null while unknown — see `readDuration`. */
  duration: number | null;
  /** 0–1. */
  progress: number;
  /** `m:ss` of the current position while playing, of the total at rest, `—:—` while unknown. */
  timeLabel: string;
  toggle: () => void;
  /** Seek from a click anywhere on a full-width track element. */
  seek: (event: MouseEvent<HTMLElement>) => void;
  /** Spread onto the `<audio>` element. */
  audioProps: {
    ref: RefObject<HTMLAudioElement | null>;
    preload: 'metadata';
    onLoadedMetadata: () => void;
    onDurationChange: () => void;
    onTimeUpdate: () => void;
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
  };
}

/** `onFirstPlay` fires once per mount, for the caller's own BI — the two surfaces report under different
 *  page names, so the hook does not log anything itself. */
export function useAudioPlayer(onFirstPlay?: () => void): AudioPlayer {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const firstPlaySent = useRef(false);

  useEffect(() => {
    const el = ref.current;
    return () => {
      // Release the lock on unmount, or a closed viewer would keep a paused element as the "active" one
      // and the next player would pause a element that is already gone.
      if (el && activeAudio === el) activeAudio = null;
    };
  }, []);

  const readDuration = () => {
    const el = ref.current;
    if (!el) return;
    // A MediaRecorder webm carries no duration header, so this stays null until playback reveals it —
    // which is why `timeLabel` has a `—:—` state at all.
    if (Number.isFinite(el.duration) && el.duration > 0) setDuration(el.duration);
  };

  const onTimeUpdate = () => {
    const el = ref.current;
    if (!el) return;
    readDuration();
    const d = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : duration;
    setProgress(d ? Math.min(1, el.currentTime / d) : 0);
  };

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      claimPlayback(el);
      void el.play().catch(() => setPlaying(false));
      if (!firstPlaySent.current) {
        firstPlaySent.current = true;
        onFirstPlay?.();
      }
    } else {
      el.pause();
    }
  };

  const seek = (event: MouseEvent<HTMLElement>) => {
    const el = ref.current;
    if (!el || !duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    // The track renders direction-aware (RTL flips the fill), but `clientX` is physical — mirror the ratio
    // when the document flows right-to-left, or a click on the visual start seeks to the end.
    const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
    const settled = rtl ? 1 - ratio : ratio;
    el.currentTime = settled * duration;
    setProgress(settled);
  };

  const timeLabel =
    playing || progress > 0
      ? formatVoiceSeconds(ref.current?.currentTime ?? 0)
      : duration != null
        ? formatVoiceSeconds(duration)
        : '—:—';

  return {
    ref,
    playing,
    duration,
    progress,
    timeLabel,
    toggle,
    seek,
    audioProps: {
      ref,
      preload: 'metadata',
      onLoadedMetadata: readDuration,
      onDurationChange: readDuration,
      onTimeUpdate,
      onPlay: () => {
        if (ref.current) claimPlayback(ref.current);
        setPlaying(true);
      },
      onPause: () => setPlaying(false),
      onEnded: () => {
        setPlaying(false);
        setProgress(0);
      },
    },
  };
}
